import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, forwardRef, Inject, Injector, LOCALE_ID, NgModule, Optional, PLATFORM_ID } from '@angular/core';
import { APP_BASE_HREF, isPlatformBrowser, registerLocaleData } from '@angular/common';
import { BrowserAnimationsModule, NoopAnimationsModule } from '@angular/platform-browser/animations';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { HttpClient, HttpClientModule, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { JwtHelperService, JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { AppComponent } from './app.component';
import { Router } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { NotfoundModule } from './errors/notfound.module';
import { SharedModule } from './shared/shared.module';
import { environment } from './../environments/environment';
import { OfferService } from './offer/offer.service';
import { UpdateService } from './update.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Page, Setup } from './shared/navigation/pages.model';
import { NavigationService } from './shared/navigation/navigation.service';
import { UserService } from './user/user.service';
import { CartService } from './shared/cart/cart.service';
import { TransferStateService } from './transfer.service';
import { USER_DATA } from './tokens';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { ArticleService } from './article/article.service';

export function jwtOptionsFactory(platformId) {
  return {
    tokenGetter: () => {
      let token = null;
      if (isPlatformBrowser(platformId)) {
        token = localStorage.getItem('access_token');
      }
      return token;
    },
    whitelistedDomains: ['localhost:4200', environment.api]
  };
};

export class JwtHttpInterceptor implements HttpInterceptor {
  constructor(@Inject(PLATFORM_ID) private platformId: Object, @Inject(LOCALE_ID) private localeId: string, @Inject(DEFAULT_CURRENCY_CODE) private currency: string) {}
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      let clone: HttpRequest<any>;
      let headers = {
        Accept: `application/json`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Content-Language': this.localeId,
        'Content-Currency': this.currency
      };
      let token = null;
      if (isPlatformBrowser(this.platformId)) {
        token = localStorage.getItem('user_token');
        let currency = 'EUR';
        if (this.localeId == 'pl') {
          currency = 'PLN';
        }
        let user_currency = localStorage.getItem('user_currency');
        if (user_currency) {
          currency = user_currency;
        }
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        } 
        // if (currency) {
          // this.currency = currency;
          // headers['Content-Currency'] = currency;
        // }
      }
      clone = request.clone({
        setHeaders: headers
      });
      return next.handle(clone);
  }
}
function initializeCurrency(transferStateService: TransferStateService, injector: Injector) {
  
  let currency = 'PLN';
  let user_currency = null;
  const platformId = injector.get(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);
  let user: any;
  if (isBrowser) {
    user = transferStateService.get('USER_DATA_KEY');
    user_currency = localStorage.getItem('user_currency');
  } else {
    user = injector.get(USER_DATA) || null;
    transferStateService.set('USER_DATA_KEY', user);
  }

  if (user) {
    currency = user.currency;
  }
  if(user_currency) {
    currency = user_currency;
  }

  return currency;
}



function initializeLocale(transferStateService: TransferStateService, injector: Injector) {
  
  let localeId = 'pl';
  const platformId = injector.get(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);
  let user: any;
  if (isBrowser) {
    user = transferStateService.get('USER_DATA_KEY');
  } else {
    user = injector.get(USER_DATA) || null;
    transferStateService.set('USER_DATA_KEY', user);
  }

  if (user) {
    // console.log(user, 'APPMODULE USER');
    localeId = user.locale;
  }

  // localeId = 'en';
  // console.log('initializeLocale', localeId);
  
  import(
    /* webpackInclude: /(pl|de|en|fr|es|it)\.mjs$/ */
    `@/../@angular/common/locales/${localeId}.mjs`
    ).then(localModule => {
      registerLocaleData(localModule.default);
  });
  

  return localeId;
}

const withChildren = {'ArticleDetailsModule': ['/:path'], 'GenericDetailsModule': ['/:path'], 'OfferDetailsModule': ['/:path', '/:path/:variant', '/:path/:variant/:configuration']};

function clearStorage() {
  localStorage.removeItem('user_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_role');
  localStorage.removeItem('user_scopes');
  localStorage.removeItem('user_state');
  localStorage.removeItem('user_currency');
  localStorage.removeItem('user_fitter');
}

function initializeBaseHref(localeId: string) {
  if (localeId != 'pl') {
    return '/'+localeId;
  } else {
    return '/';
  }
}


function translatePaginator(localeId: string) {

  const rangeLabel = (page: number, pageSize: number, length: number) => {

    let label = 'z';
    if (localeId == 'en') {
      label = 'of';
    } else if (localeId == 'de') {
      label = 'aus';
    } else if (localeId == 'fr') {
      label = 'de';
    } else if (localeId == 'it' || localeId == 'es') {
      label = 'con';
    }

    if (length == 0 || pageSize == 0) { return `0 ${label} ${length}`; }
    
    length = Math.max(length, 0);
  
    const startIndex = page * pageSize;
  
    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex = startIndex < length ?
        Math.min(startIndex + pageSize, length) :
        startIndex + pageSize;
  
    return `${startIndex + 1} - ${endIndex} ${label} ${length}`;
  }

  const paginatorIntl = new MatPaginatorIntl();
  paginatorIntl.getRangeLabel = rangeLabel;
  return paginatorIntl;
}

function initializeAppFactory(httpClient: HttpClient, navigationService: NavigationService, router: Router, jwtHelperService: JwtHelperService, localeId: string): () => Observable<any> {

  return () => httpClient.get<Setup>(environment.api + '/settings/'+localeId)
    .pipe(
      tap(setup => { 
      const navigation: Page[] = [];
      const navigationFooter: Page[] = [];
      const navigationInvisible: Page[] = [];
      const config = router.config;

      if (setup.settings) { //set homepage data
        config[0].data.title = setup.settings.seo_title;
        config[0].data.content = setup.settings.content;
        config[0].data.description = setup.settings.description;
        config[0].data.keywords = setup.settings.keywords;
      }

      if (isPlatformBrowser(this.platformId)) {
        const token = localStorage.getItem('user_token');
        if (token) {
          if (jwtHelperService.isTokenExpired(token)) {
            clearStorage();
          }
        } else {
          clearStorage();
        }
      }

      recursiveLoop(config, setup.menu, navigation, navigationFooter, navigationInvisible);

      router.resetConfig(config);
      router.initialNavigation();

      navigationService.navigation.next({
        menu: navigation,
        footer: navigationFooter,
        invisible: navigationInvisible,
        settings: setup.settings,
        payments: setup.payments.items || [],
        carriers: setup.carriers.items || []
      });

      })
  );
}; 

function prepareAddRoute(config: any, page: any) {
  const stackedRouteExist = config.find(x => x.path === page.path);
  if (!stackedRouteExist && page.path != undefined && page.path.length !== 0) {
    let moduleParts = [];
    if (page.module != null) {
      moduleParts = page.module.split('#');
    }

    if (page.template == 'wiki') {
      if (page.module == './article/article.module#ArticleModule') {
        moduleParts = ['./wiki/wiki.module', 'WikiModule'];
      }
      if (page.module == './article/article-details.module#ArticleDetailsModule') {
        moduleParts = ['./wiki/wiki.module', 'WikiDetailsModule'];
      }
      // page.template = null;
    }

    if (moduleParts[0] != 'container') {

      let path = page.path;
      if (withChildren[moduleParts[1]]) { 
        
        withChildren[moduleParts[1]].forEach(childpath => {
          path = page.path + childpath;
          pushPage(config, page, path, moduleParts[1]);  
        });
                
      } else {     
        pushPage(config, page, path, moduleParts[1]);  
      }
    }
  }
}

function pushPage(config, page, path, module) {
  const newpage = {path: path, loadChildren: () => import('@digitalgrid').then(m => m[module]), data: {
    id: page.id,
    module: module,
    level: page.level,
    parent: page.parent_id,
    name: page.name,
    path: page.path,
    path_id: page.path_id,
    title: page.title ? page.title : page.name,
    content: page.content,
    description: page.description,
    keywords: page.keywords,
    extra_field_1: page.extra_field_1,
    extra_field_2: page.extra_field_2,
    extra_field_3: page.extra_field_3,
    extra_field_4: page.extra_field_4,
    extra_field_5: page.extra_field_5,
    template: page.template,
    hideBredcrumbs: (page.template && page.template != 'wiki' ? true : false),
    search_params: page.search_params,
    photos_directory: page.photos_directory,
    canonical: environment.base + '/' + page.path
  }};
  config.unshift(newpage);
}

function recursiveLoop(config: any[], data: any[], navigation, navigationFooter, navigationInvisible) {


  var r = data.forEach((page: Page) => {
    //router part
    prepareAddRoute(config, page);
    //navigation part

    let children = [];
    let footer_children = [];
    let invisible_children = [];

    if (+page.visible > 0 && page.visible_in.includes('main')) {
      if (page.children) {
        const copy = JSON.parse(JSON.stringify(page.children));
        children = recursiveChildren(config, page.children, navigation, navigationFooter, navigationInvisible, './offer/offer.module#DummyModule', '!==', 'main');
      }
      const npage = { ...page };
      npage.children = [];
      if (children.length) npage.children = children;
      navigation.push(npage);
    }

    if (+page.visible > 0 && page.visible_in.includes('footer')) {
      if (page.children) {
        footer_children = recursiveChildren(config, page.children, navigation, navigationFooter, navigationInvisible, './offer/offer.module#DummyModule', '!==', 'footer');
      }
      const npage = { ...page };
      npage.children = [];
      if (footer_children.length) npage.children = footer_children;
      navigationFooter.push(npage);
    }

    if (+page.visible > 0) {
      if (page.children) {
        invisible_children = recursiveChildren(config, page.children, navigation, navigationFooter, navigationInvisible, 'all', 'all', 'all');
      }
      const npage = { ...page };
      npage.children = [];
      if (invisible_children.length) npage.children = invisible_children;
      navigationInvisible.push(npage);
    }
  })

}

function recursiveChildren(config: any[], data: any[], navigation, navigationFooter, navigationInvisible, type: string, modifier: string, inside: string) {

  const children = [];
  data.forEach((page: Page) => {

    prepareAddRoute(config, page);

    if (modifier === 'all') {

      if (+page.visible > 0) {
        let page_children = [];
        if (page.children) {
          if (page.children.length) {
            page_children = recursiveChildren(config, page.children, navigation, navigationFooter, navigationInvisible, 'all', 'all', 'all');
            // if (page.path == 'baza-wiedzy') {
            //   console.log(page, page_children);

            // }
          } 
        }
        const npage = { ...page };
        npage.children = page_children;
        children.push(npage);
      }
    } 
    else if (modifier === '===') {
      if (page.module === type && +page.visible > 0 && page.visible_in.includes(inside)) {
        let page_children = [];
        if (page.children) {
          if (page.children.length) {
            page_children = recursiveChildren(config, page.children, navigation, navigationFooter, navigationInvisible, type, '===', inside);
          } 
        }
        const npage = { ...page };
        npage.children = page_children;
        children.push(npage);
      }
    } else {
      if (page.module !== type && +page.visible > 0 && page.visible_in.includes(inside)) {
        let page_children = [];
        if (page.children) {
          page_children = recursiveChildren(config, page.children, navigation, navigationFooter, navigationInvisible, type, '!==', inside);
        }
        const npage = { ...page };
        npage.children = page_children;
        children.push(npage);
      }
    }
  })
  return children;
};

@NgModule({
  imports: [
    BrowserModule.withServerTransition({ appId: 'app-root' }),
    TransferHttpCacheModule,
    HttpClientModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    // HammerModule,
    NotfoundModule, 
    SharedModule.forRoot(),
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [PLATFORM_ID]
      }
    }),
    ServiceWorkerModule.register('/ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
  declarations: [AppComponent],
  providers: [UpdateService, OfferService, ArticleService, UserService, CartService,
  {provide: LOCALE_ID, useFactory: initializeLocale, deps: [TransferStateService, Injector]},
  {provide: DEFAULT_CURRENCY_CODE, useFactory: initializeCurrency, deps: [TransferStateService, Injector]},
  {provide: APP_BASE_HREF, useFactory: initializeBaseHref, deps: [LOCALE_ID]}, //
  {provide: APP_INITIALIZER, useFactory: initializeAppFactory, deps: [HttpClient, NavigationService, Router, JwtHelperService, LOCALE_ID], multi: true}, //
  {provide: HTTP_INTERCEPTORS, useClass: JwtHttpInterceptor, deps: [PLATFORM_ID, LOCALE_ID, DEFAULT_CURRENCY_CODE], multi: true },
  { provide: MatPaginatorIntl, useFactory: translatePaginator, deps: [LOCALE_ID] }
  ],
  bootstrap: [AppComponent]

})
export class AppModule { }
